import { errorFrom } from "@/lib/errors";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/primitives/Button";
import { activateSceneImage, api, generateSceneImage, getGenerationCosts, listScenes, sceneImageUrl, uploadSceneImage, type AspectRatio, type Scene } from "@/lib/api";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp";

export function SceneImagePanel({
  scene,
  index,
  aspect,
  disabled,
  onUploaded,
}: {
  scene: Scene;
  index: number;
  aspect: AspectRatio;
  disabled?: boolean;
  onUploaded: (scene: Scene) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(false);
  const [cost, setCost] = useState<number | null>(null);
  const genLock = useRef(false);
  const image = scene.image;
  const locked = !!scene.image_generating;  // generation running server-side (also after refresh)
  const history = scene.image_history ?? [];
  const version = image ? `${image.uploaded_at}:${image.storage_path.slice(-12)}` : undefined;

  // Authenticated fetch (Bearer/cookie) -> object URL, so <img> never depends on cookies alone.
  useEffect(() => {
    if (!version) {
      setSrc(null);
      return;
    }
    let url: string | null = null;
    let cancelled = false;
    api
      .get(sceneImageUrl(scene.id, version), { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => !cancelled && setSrc(null));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [scene.id, version]);

  useEffect(() => {
    if (!locked) return;
    const t = setInterval(async () => {
      try {
        const fresh = (await listScenes(scene.project_id)).find((x) => x.id === scene.id);
        if (fresh && !fresh.image_generating) onUploaded(fresh);
      } catch {
        /* retry on next tick */
      }
    }, 4000);
    return () => clearInterval(t);
  }, [locked, scene.id, scene.project_id, onUploaded]);

  const openConfirm = async () => {
    setConfirming(true);
    if (cost === null) getGenerationCosts().then((c) => setCost(c.image_usd)).catch(() => setCost(null));
  };

  const generate = async () => {
    if (genLock.current) return;
    genLock.current = true;
    setConfirming(false);
    setGenerating(true);
    setGenError(false);
    try {
      const updated = await generateSceneImage(scene.id);
      onUploaded(updated);
      if (updated.image_generating) toast.message(t("sceneImage.alreadyGenerating"));
      else toast.success(t("sceneImage.readyToast", { n: index + 1 }));
    } catch (err: any) {
      setGenError(true);
      toast.error(errorFrom(err, "sceneImage.generateFailed"));
    } finally {
      setGenerating(false);
      genLock.current = false;
    }
  };

  const activate = async (storage_path: string) => {
    try {
      onUploaded(await activateSceneImage(scene.id, storage_path));
    } catch {
      toast.error(t("sceneImage.activateFailed"));
    }
  };

  const aiBusy = disabled || uploading || generating || locked;

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type)) {
      toast.error(t("sceneImage.badFormat"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("sceneImage.tooLarge"));
      return;
    }
    setUploading(true);
    try {
      onUploaded(await uploadSceneImage(scene.id, file));
      toast.success(`Immagine della scena ${index + 1} aggiornata`);
    } catch (err: any) {
      toast.error(errorFrom(err, "sceneImage.uploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`scene-image-panel-${index}`} className="w-full">
      <span className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {t("sceneImage.title")}
      </span>
      <div
        data-testid={`scene-image-frame-${index}`}
        data-aspect={aspect}
        className={cn(
          "relative mx-auto overflow-hidden rounded-lg border bg-[#05070D]",
          aspect === "9:16" ? "aspect-[9/16] w-32 sm:w-36" : "aspect-video w-full",
          image ? "border-[#2E7BFF]/40 shadow-[0_0_24px_-10px_rgba(46,123,255,0.6)]" : "border-dashed border-[#2E7BFF]/25",
        )}
      >
        {image && !src ? (
          <div className="grid h-full w-full place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-[#2E7BFF]" />
          </div>
        ) : image && src ? (
          <img
            data-testid={`scene-image-preview-${index}`}
            src={src}
            alt={`Scena ${index + 1}`}
            className="h-full w-full object-contain"
          />
        ) : (
          <button
            type="button"
            data-testid={`scene-image-empty-${index}`}
            disabled={disabled || uploading}
            onClick={() => inputRef.current?.click()}
            className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground transition-colors hover:bg-[#2E7BFF]/5 hover:text-foreground disabled:opacity-50"
          >
            <ImagePlus className="h-6 w-6 text-[#2E7BFF]" />
            <span className="px-3 text-center text-[11px] uppercase tracking-wider">Nessuna immagine</span>
          </button>
        )}
        {(uploading || generating || locked) && (
          <div data-testid={`scene-image-generating-${index}`} className="absolute inset-0 grid place-items-center bg-[#05070D]/75">
            <span className="flex flex-col items-center gap-2 text-[10px] uppercase tracking-wider text-[#7FD4FF]">
              <Loader2 className="h-5 w-5 animate-spin" />
              {uploading ? t("common.loading") : t("sceneImage.generating")}
            </span>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid={`scene-image-input-${index}`}
        onChange={pick}
      />
      <Button
        type="button"
        variant="secondary"
        size="sm"
        data-testid={`scene-image-upload-btn-${index}`}
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        className="mt-3 w-full"
      >
        {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : image ? <RefreshCw className="h-3.5 w-3.5" /> : <ImagePlus className="h-3.5 w-3.5" />}
        <span className="ml-1.5">{image ? t("sceneImage.change") : t("sceneImage.upload")}</span>
      </Button>
      <Button
        type="button"
        size="sm"
        data-testid={`scene-image-ai-btn-${index}`}
        disabled={aiBusy}
        onClick={openConfirm}
        className="mt-2 w-full"
      >
        {generating || locked ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
        <span className="ml-1.5">{genError ? t("common.retry") : t("sceneImage.generateAi")}</span>
      </Button>
      <p data-testid={`scene-image-status-${index}`} className="mt-2 text-xs">
        {generating || locked ? (
          <span className="text-[#7FD4FF]">{t("sceneImage.generating")}</span>
        ) : genError ? (
          <span className="text-[#FF8A8A]">{t("sceneImage.error")}</span>
        ) : image?.source === "ai" ? (
          <span className="text-[#3DDC84]">{t("sceneImage.readyAi")}</span>
        ) : image ? (
          <span className="text-muted-foreground">{t("sceneImage.uploaded")}</span>
        ) : (
          <span className="text-muted-foreground">{t("sceneImage.none")}</span>
        )}
      </p>
      {image && history.length > 0 && (
        <select
          data-testid={`scene-image-version-${index}`}
          value={image.storage_path}
          disabled={aiBusy}
          onChange={(e) => e.target.value !== image.storage_path && activate(e.target.value)}
          className="field-premium mt-2 h-8 !rounded-md px-2 text-xs"
        >
          <option value={image.storage_path}>{t("sceneImage.versionActive", { n: history.length + 1 })}</option>
          {[...history].reverse().map((h, i) => (
            <option key={h.storage_path} value={h.storage_path}>
              {t("sceneImage.version", { n: history.length - i })} · {h.source === "ai" ? "AI" : t("sceneImage.uploadedShort")}
            </option>
          ))}
        </select>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setConfirming(false)}>
          <div className="glass-panel w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid={`scene-image-confirm-${index}`}>
            <h3 className="font-display text-base font-semibold uppercase tracking-wide text-foreground">
              {t("sceneImage.generateAi")} · {t("sceneImage.sceneN", { n: index + 1 })}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("sceneImage.confirmBody")} <span className="text-[#7FD4FF]">{aspect}</span>.
              {cost !== null && (
                <> {t("common.predictedCost")}: <strong className="text-[#7FD4FF]">${cost.toFixed(2).replace(".", ",")}</strong>.</>
              )}{" "}
              {t("common.startsAfterConfirm")}
              {image && ` ${t("sceneImage.keepPrevious")}`}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirming(false)} data-testid={`scene-image-confirm-cancel-${index}`}>{t("common.cancel")}</Button>
              <Button size="sm" onClick={generate} data-testid={`scene-image-confirm-accept-${index}`}>{t("common.confirmGenerate")}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
